<template>
  <div>
    <t-divider> </t-divider>
    <div>
      <t-form-item name="desc" :rules="requireRule" :label="$t('任务描述')">
        <t-textarea
          v-model="formData.desc"
          class="prompt-resizable-textarea"
          maxlength="200"
          :placeholder="
            $t(
              '请输入任务描述，让大模型理解什么情况下应该调用该任务流。如：识别到用户想要退款的时候触发。如：只是询问退款政策，不要触发。',
            )
          "
        />
      </t-form-item>
      <p style="margin: 14px 0">
        <text-with-mark :text="$t('触发类型')" />
      </p>
      <div class="trigger-method-container">
        <div
          v-for="item in triggerMethods"
          :key="item.value"
          :class="{
            'trigger-method-item': true,
            'trigger-method-item-active': formData.triggerMethod === item.value,
          }"
          @click="handleClickTriggerMethod(item.value)"
        >
          <div class="trigger-method-item-header">
            <img
              :src="item.icon"
              :alt="item.label"
              class="trigger-method-icon"
            />
            <span class="trigger-method-name">{{ item.label }}</span>
          </div>
          <p class="trigger-method-description">{{ item.desc }}</p>
        </div>
      </div>
    </div>
    <div v-if="formData.triggerMethod === 0">
      <p style="margin: 14px 0">
        <text-with-mark :text="$t('用户意图')" />
      </p>
      <p style="margin: 14px 0">
        <text-with-mark
          :hidden-mark="true"
          :text="$t('举个例子，让大模型更好理解')"
        />
      </p>
      <t-table
        class="no-border-table"
        :data="formData.examples"
        row-key="id"
        drag-sort="row-handler"
        lazy-load
        size="small"
        :columns="columns"
        vertical-align="top"
        :show-header="false"
        @dragSort="onDragSort"
      >
        <template #example="{ rowIndex }">
          <t-form-item :name="`examples[${rowIndex}]`" :required-mark="false">
            <t-space :size="8" class="example-space">
              <t-input
                v-model="formData.examples[rowIndex].q"
                maxlength="200"
                style="width: 385px"
              />
              <CloseCircleIcon
                size="16"
                style="cursor: pointer"
                @click="deleteExamples(rowIndex)"
              />
            </t-space>
          </t-form-item>
        </template>
        <template #bottomContent>
          <t-form-item
            class="no-border-table-bottom"
            name="examples"
            :rules="examplesRules"
            :status-icon="false"
          >
            <div v-if="formData.examples.length < 20">
              <t-link theme="primary" hover="color" @click="addExamples">{{
                $t("添加例句")
              }}</t-link>
            </div>
          </t-form-item>
        </template>
      </t-table>
    </div>
    <div v-else-if="formData.triggerMethod === 1">
      <t-form-item :label="$t('自定义事件')" name="eventId">
        <t-select
          v-model="formData.eventId"
          :options="triggerEventOptions"
          filterable
        />
      </t-form-item>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { inject, onMounted, reactive, Ref, watch } from "vue";
import { CloseCircleIcon, MoveIcon } from "tdesign-icons-vue-next";
import TriggerEventIcon from "../img/triggerEvent.svg";
import TriggerIntentionIcon from "../img/triggerIntention.svg";
import { toString, map, merge, cloneDeep, compact, trim } from "lodash-es";
import { t } from "@/utils/i18n";
import {
  Example,
  UserIntentNodeData,
  UserIntentFormData,
} from "@/types/userIntentNodeType";

const triggerMethods = [
  {
    value: 1,
    label: t("事件触发"),
    icon: TriggerEventIcon,
    desc: t("满足自定义的事件后运行流程"),
  },
  {
    value: 0,
    label: t("意图识别"),
    icon: TriggerIntentionIcon,
    desc: t("识别到用户意图后运行流程"),
  },
];

type OptionType = Array<{ value: string; label: string }>;

const emit = defineEmits(["updateData"]);
const getTaskTriggerEventIdFn = inject<Ref<() => Promise<OptionType>>>(
  "getTaskTriggerEventIds",
);

const examplesRules = [
  {
    validator: (examples) => {
      const _arr = compact(examples);
      if (new Set(_arr).size !== _arr.length) {
        return false;
      }
      return true;
    },
    message: t("问法不能重复"),
    trigger: "change",
  },
];

const triggerEventOptions = reactive<OptionType>([]);

const getTriggerEventOptions = async () => {
  if (getTaskTriggerEventIdFn?.value) {
    getTaskTriggerEventIdFn.value().then((data) => {
      console.log("data", data);
      triggerEventOptions.splice(0, triggerEventOptions.length, ...data);
    });
  }
};

const props = defineProps<{
  data: UserIntentNodeData;
}>();

const formData = reactive<UserIntentFormData>({
  examples: map(
    props.data.examples.length ? props.data.examples : [""],
    (v) => ({
      id: toString(Math.random()),
      q: v,
    }),
  ),
  desc: props.data.desc ?? "",
  eventId: props.data?.eventTriggerConfig?.eventId?.toString() || "",
  triggerMethod: props.data.triggerMethod ?? 0,
});

const onDragSort = (params: { currentData: Example[] }) => {
  formData.examples = cloneDeep(params.currentData);
};

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const columns = [
  {
    colKey: "example",
  },
];

const handleClickTriggerMethod = (method: number) => {
  formData.triggerMethod = method;
};

const addExamples = () => {
  formData.examples.push({
    id: toString(Math.random()),
    q: "",
  });
};

const deleteExamples = (index: number) => {
  formData.examples.splice(index, 1);
};

onMounted(() => {
  getTriggerEventOptions();
});

watch(
  formData,
  (newVal) => {
    emit(
      "updateData",
      merge({}, newVal, { examples: map(newVal.examples, "q") }),
    );
    console.log(formData, 1113332);
    if (newVal.triggerMethod === 1) {
      getTriggerEventOptions();
    }
  },
  { deep: true },
);
</script>
<style lang="less" scoped>
:deep(.flow-t-table td) {
  border: none;
}
:deep(.flow-t-table.flow-t-size-s td) {
  padding: 0px;
}
:deep(.flow-t-table tr) {
  height: 52px;
}
:deep(.prompt-resizable-textarea .flow-t-textarea__inner) {
  min-height: 55px;
  max-height: 220px;
  resize: vertical;
}
:deep(.example-space) {
  display: flex;
  align-items: center;
}
:deep(.flow-t-form__controls.flow-t-is-error) {
  min-height: unset;
  .flow-t-form__controls-content {
    min-height: unset;
  }
}

.no-border-table-bottom {
  padding-bottom: 30px;
}

.trigger-method-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;

  .trigger-method-item {
    padding: 12px;
    border-radius: 8px;
    width: 45%;
    border: 1px solid var(---Gray-Light-3, rgba(230, 233, 240, 1));

    .trigger-method-item-header {
      display: flex;
      align-items: center;
      .trigger-method-icon {
        width: 20px;
        height: 20px;
      }

      .trigger-method-name {
        font-size: 12px;
        line-height: 20px;
        font-weight: 400;
        margin-left: 4px;
      }
    }

    .trigger-method-description {
      font-size: 12px;
      line-height: 20px;
      color: var(---Gray-Light-7, rgba(165, 169, 176, 1));
      margin: 8px 0px 0px 0px;
    }
  }
}

.trigger-method-item-active {
  background: var(---Theme-Light-1, rgba(235, 243, 255, 1));
  border: 1px solid var(---Theme-Light-6, rgba(28, 102, 229, 1)) !important;

  .trigger-method-name {
    font-weight: 500;
    color: var(---Theme-Light-6, rgba(28, 102, 229, 1));
  }
}
</style>
